import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createDeviceIdentificationTracker } from '#~/analytics/domains/device-identification.js';
import { Sel, STag } from '#~/stag/tag.js';
import { findOther, getModelRows, NotListedActionLabel } from './search.js';
import type { ModelProps } from './types.js';
import type { Collection } from '#~/api/device/registry/index.js';
import { useNotListedFlow } from './not-listed.jsx';
import { DriverStatusBadge, useDriverStatus } from '#~/device/components/driver-status/index.js';
import { Combobox } from '@studio/design-system/combobox';
import { Tag } from '@studio/design-system/tag';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isEmpty } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { memo, useMemo, useRef, useState } from 'react';

/**
 * Model item with a driver status badge. ENG-4924: a model we recognise but have no driver for
 * shows a "Not yet supported" tag instead, because it has no driver whose status we could report.
 */
const ModelItem = ({ model }: { model: M_Model }): ReactElement => {
  // Called unconditionally to satisfy the rules of hooks; ignored for not-yet-supported models.
  const driverStatus = useDriverStatus(model);

  return (
    <div className='flex w-full items-center justify-between gap-8'>
      <span>{model.name}</span>
      {model.notYetSupported ? (
        <Tag
          className='px-12 py-6 text-sm font-medium'
          message='Not yet supported'
          severity='info'
          data-stag='config.video-device.brand-model.not-yet-supported-tag'
        />
      ) : (
        driverStatus && (
          <DriverStatusBadge
            status={driverStatus}
            data-stag='config.video-device.brand-model.status-badge'
          />
        )
      )}
    </div>
  );
};

const getModelKey = (model: M_Model): string => model.id;
const getModelLabel = (model: M_Model): string => model.name;
const getModelAttributes = (model: M_Model) =>
  STag(Sel.ConfigVideoDeviceBrandModel, 'model-option', model.id);
const renderModel = (model: M_Model): ReactElement => <ModelItem model={model} />;

/**
 * `<Model>` component — type-ahead model search scoped to one brand (ENG-4831).
 *
 * Rows keep the fidelity badge / "Not yet supported" tag; the fixed "My camera
 * isn't listed" trailing action replaces scrolling to the brand's "Other" row.
 */
export const Model = memo(
  ({
    brandId,
    brandName,
    confirmOther = true,
    disabled,
    initialQuery,
    models,
    onIdentified,
    onNotListed,
    openOnMount,
    selection,
    size,
    ...props
  }: ModelProps): ReactElement => {
    const { track } = useAnalytics();
    // Bumped to remount the combobox when a not-listed detour ends without a
    // pick, so its input drops the un-committed label (same trick the Select used).
    const [magicNumber, setMagicNumber] = useState(0);
    // ENG-4832: "Back returns to the list, keeping the search text". The
    // combobox resets its own query when it commits a row or the trailing
    // action, so the live query is mirrored here, snapshotted when a detour
    // opens, and handed back as `initialQuery` on the remount after Back.
    // Starts from the caller's own `initialQuery`.
    const [query, setQuery] = useState(initialQuery ?? '');
    const [restoredQuery, setRestoredQuery] = useState(initialQuery);
    const detourQuery = useRef<string | undefined>(undefined);
    const resetInput = useLatestFn((keepQuery?: string) => {
      setRestoredQuery(keepQuery);
      setMagicNumber((µ) => µ + 1);
    });
    const restoreInput = useLatestFn(() => resetInput(detourQuery.current));

    const items = useMemo(() => getModelRows(models), [models]);

    // ENG-4832 C1/C2, scoped to this brand: the model field cannot change the
    // brand, so "Did you mean" only offers this brand's models.
    const flowBrands = useMemo(
      () =>
        brandId
          ? ({
              generic: { id: brandId, name: brandName ?? '' },
              items: { [brandId]: { id: brandId, name: brandName ?? '' } }
            } as unknown as Collection<M_Brand>)
          : undefined,
      [brandId, brandName]
    );
    const flowModels = useMemo(
      () => (brandId && models ? { [brandId]: models } : undefined),
      [brandId, models]
    );
    const flow = useNotListedFlow({
      brands: flowBrands,
      models: flowModels,
      onBack: restoreInput,
      onContinue: (identification) => {
        const other = findOther(models);

        // ENG-4832: the typed text and `identified_by` travel with the generic
        // model so the caller persists them (settings: StudioDevice 13–15).
        onIdentified?.(identification);

        if (other && other.id !== selection.value) {
          selection(other.id);
        } else {
          resetInput();
        }
      },
      onDidYouMean: ({ modelId }) => {
        if (modelId !== selection.value) {
          selection(modelId);
        }
      }
    });

    // Caller flow first (the add wizard); else this picker's own C1/C2; else
    // (`confirmOther` false — no dialog possible) the generic model directly.
    const handleNotListed = useLatestFn((nextQuery: string) => {
      if (onNotListed) {
        onNotListed(nextQuery);
      } else if (confirmOther) {
        detourQuery.current = nextQuery;
        flow.open({ initial: { brand: brandName ?? '', model: nextQuery } });
      } else {
        const other = findOther(models);

        if (other && other.id !== selection.value) {
          selection(other.id);
        }
      }
    });

    const commitModel = useLatestFn((model: M_Model) => {
      if (model.id === selection.value) {
        return;
      }

      // ENG-4924: a "Not yet supported" row is never the selected model.
      // Picking it opens C2 prefilled ("<Brand Model> isn't supported yet");
      // Continue saves the generic model with the typed names and
      // `not_yet_supported_picked`, Back/close restores the search.
      if (model.notYetSupported && !onNotListed && confirmOther) {
        detourQuery.current = query;
        resetInput();
        flow.open({
          initial: { brand: brandName ?? '', model: model.name },
          openedFrom: 'not_yet_supported_pick',
          step: 'c2'
        });

        return;
      }

      // A caller with its own flow (the add wizard) diverts the tagged pick and never
      // echoes it back as the value, so drop the label the combobox just showed: after
      // Back the field must not look selected.
      if (model.notYetSupported && onNotListed) resetInput();

      selection(model.id);
    });

    const handleNoMatches = useLatestFn((query: string) => {
      // ENG-4831: brand_name is the brand this model search is scoped to.
      createDeviceIdentificationTracker(track).trackDeviceSearchNoMatches({
        search_text: query,
        search_field: 'model',
        brand_name: brandName ?? ''
      });
    });

    const trailingAction = useMemo(
      () => ({
        attributes: STag(Sel.ConfigVideoDeviceBrandModel, 'not-listed', 'model'),
        label: NotListedActionLabel,
        onSelect: handleNotListed
      }),
      [handleNotListed]
    );

    const placeholder = disabled
      ? 'Select a brand'
      : isEmpty(models) && brandId
        ? 'Loading models…'
        : brandName
          ? `Search ${brandName} models`
          : 'Search models';

    return (
      <>
        <Combobox<M_Model>
          {...props}
          {...STag(Sel.ConfigVideoDeviceBrandModel, 'model-input')}
          disabled={disabled}
          getKey={getModelKey}
          getLabel={getModelLabel}
          getOptionAttributes={getModelAttributes}
          items={items}
          key={`model-combobox-gen-${magicNumber}`}
          onChange={commitModel}
          onNoMatches={handleNoMatches}
          onQueryChange={setQuery}
          placeholder={placeholder}
          renderItem={renderModel}
          size={size}
          trailingAction={trailingAction}
          value={selection.value}
          // exactOptionalPropertyTypes: omit rather than pass undefined.
          {...(restoredQuery === undefined ? {} : { initialQuery: restoredQuery })}
          // Mount-time open/focus is for the first render only: a remount from
          // resetInput happens while a dialog is opening and must not pop the
          // list open behind it or take its focus.
          {...(openOnMount === undefined || magicNumber > 0 ? {} : { openOnMount })}
          {...(magicNumber > 0 ? { focusOnMount: false } : {})}
        />
        {flow.element}
      </>
    );
  },
  // Callback props are documented as stable, so only data props gate a re-render.
  (prev, next) =>
    prev.models === next.models &&
    prev.selection === next.selection &&
    prev.brandName === next.brandName &&
    prev.disabled === next.disabled &&
    prev.focusOnMount === next.focusOnMount &&
    prev.initialQuery === next.initialQuery &&
    prev.openOnMount === next.openOnMount &&
    prev.onIdentified === next.onIdentified &&
    prev.onNotListed === next.onNotListed
);
